import { ComponentIds } from '@inter-ikea-core/core';
import { addVectors, getDirectedUnitVector } from './vectorUtils';
import { isDefined } from './isDefined';
/**
 * Returns the forward vector of the entity's world transform component.
 *
 * @param entity - Entity containing world transform component
 * @returns Forward vector of the entity's world transform component or null if not found
 */
const getEntityForwardVector = (entity) => {
    const transform = entity.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform;
    if (!transform) {
        return null;
    }
    return {
        x: transform.forward.X,
        y: transform.forward.Y,
        z: transform.forward.Z
    };
};
/**
 * Calculates the normalized direction vector from an array of entities.
 *
 * @param entities - Array of entities containing world transform components
 * @returns A normalized direction vector (IVector3). If no valid direction can be calculated,
 * returns a default vector pointing backwards (0, 0, -1)
 *
 * @remarks
 * The function:
 * 1. Accumulates the forward vectors from all entities world transform components
 * 2. Normalizes the resulting vector to create a unit vector
 * 3. Returns the normalized direction or a default if magnitude is zero
 */
export const getNormalizedDirectionFromEntities = (entities) => {
    const forwardVectors = entities.map(entity => getEntityForwardVector(entity)).filter(isDefined);
    return getDirectedUnitVector(addVectors(forwardVectors));
};
/**
 *
 * @returns Default angle adjustment values for camera direction.
 * The default values are set to {x: -0.4, y: -0.1, z: 0}.
 */
export const getDefaultAngleAdjustment = () => ({ x: -0.4, y: -0.1, z: 0 });
/**
 * Calculates and adjusts the camera direction for photographing a group of entities.
 * The function first gets a normalized direction from the entities, then adjusts it
 * based on the provided angles for better composition, and finally re-normalizes
 * the direction vector.
 *
 * @param entities - Array of Entity objects to determine the photo direction for
 * @param angleAdjustment - Object containing x, y, and z angle adjustments
 * @returns VectorXYZ - A normalized direction vector representing the optimal camera angle
 */
export const getAngleAdjustedDirection = (entities, angleAdjustment) => {
    const normalizedDirection = getNormalizedDirectionFromEntities(entities);
    // Re-normalize after adjustment
    return getDirectedUnitVector(addVectors([normalizedDirection, angleAdjustment]));
};
//# sourceMappingURL=cameraUtils.js.map